import { useCallback, useEffect, useRef, useState } from 'react';

// useGeminiPCM — Gemini Live transport for the customer voice call.
//
// Capture path (browser → server → Gemini):
//   getUserMedia → AudioContext → AudioWorklet (pcm-encoder) → base64 →
//   ws.send({type:'audio', data}) → server bridges to sendRealtimeInput.
//
// Playback path (Gemini → server → browser):
//   ws.onmessage {type:'audio', data} → base64 decode → Int16Array →
//   Float32Array → AudioBufferSourceNode scheduled at the end of the jitter
//   queue. The scheduling math keeps chunks gapless even when the network
//   delivers a burst of small chunks.
//
// Mute: the worklet's mute flag stops it from emitting frames (we still
// receive playback from Gemini). Saves upstream tokens vs sending silent
// audio while muted.

// Worklet lives in public/ so CRA copies it to the build output as-is.
const PCM_WORKLET_URL = '/audio-worklets/pcm-encoder.js';

const INPUT_SAMPLE_RATE  = 16000;
const OUTPUT_SAMPLE_RATE = 24000;

// Decode a base64 string to a Uint8Array. atob() returns a binary string;
// each char's code is the corresponding byte value.
function b64ToBytes(b64) {
  const bin = atob(b64);
  const len = bin.length;
  const out = new Uint8Array(len);
  for (let i = 0; i < len; i++) out[i] = bin.charCodeAt(i);
  return out;
}

// Encode a Uint8Array to base64 in chunks so the inline String.fromCharCode
// argument list doesn't blow the stack for ~1 KB+ frames.
function bytesToB64(bytes) {
  let bin = '';
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    bin += String.fromCharCode.apply(null, bytes.subarray(i, i + chunk));
  }
  return btoa(bin);
}

// PCM 16-bit LE → Float32 [-1, 1]
function pcm16ToFloat32(int16) {
  const out = new Float32Array(int16.length);
  for (let i = 0; i < int16.length; i++) {
    const v = int16[i];
    out[i] = v < 0 ? v / 0x8000 : v / 0x7fff;
  }
  return out;
}

export function useGeminiPCM(handlers) {
  // Handlers are stored in a ref so the hook can read the latest version
  // inside long-lived WS callbacks without re-rendering on every change.
  // Callers pass { onStatus, onClose, onError, onInterrupted } - all optional.
  const handlersRef = useRef(handlers);
  handlersRef.current = handlers;

  // One identity owns startup settlement and every media resource. A stale
  // async continuation can only clean up its own attempt, never a retry.
  const attemptRef = useRef(null);
  const [isMuted, setMuted] = useState(false);

  function callHandler(name, ...args) {
    const fn = handlersRef.current && handlersRef.current[name];
    if (fn) try { fn(...args); } catch (e) { console.warn(`[gemini-pcm] ${name} handler threw`, e); }
  }

  // Helper that posts {type:'mute'|'unmute'} both to the worklet AND to the
  // server (so the server can stop forwarding audio to Gemini while muted).
  const setMutedBoth = useCallback((mute) => {
    setMuted(mute);
    const attempt = attemptRef.current;
    const node = attempt?.captureNode;
    if (node && node.port) node.port.postMessage({ type: mute ? 'mute' : 'unmute' });
    const ws = attempt?.ws;
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({ type: mute ? 'mute' : 'unmute' }));
    }
  }, []);

  const teardown = useCallback((attempt = attemptRef.current) => {
    if (!attempt || attempt.ended) return;
    attempt.ended = true;
    // Settle and cancel the deadline BEFORE removing the close/error handlers.
    attempt.settleStartup?.(new Error('Gemini startup cancelled'));
    const ws = attempt.ws;
    if (ws) { ws.onopen = null; ws.onerror = null; ws.onclose = null; ws.onmessage = null; }
    try { ws && ws.close(); } catch {}
    for (const src of attempt.playbackSources) {
      try { src.stop(); src.disconnect(); } catch {}
      src.onended = null;
    }
    attempt.playbackSources.clear();
    if (attempt.captureNode?.port) attempt.captureNode.port.onmessage = null;
    try { attempt.captureNode && attempt.captureNode.disconnect(); } catch {}
    try { attempt.captureSrc && attempt.captureSrc.disconnect(); } catch {}
    try { attempt.stream.getTracks().forEach((t) => t.stop()); } catch {}
    try { attempt.captureCtx && attempt.captureCtx.close(); } catch {}
    try { attempt.playCtx && attempt.playCtx.close(); } catch {}
    if (attemptRef.current === attempt) attemptRef.current = null;
  }, []);

  // start(mediaStream, wsUrl) resolves only after ready. Cancellation also
  // settles it during worklet loading, before a browser socket even exists.
  const start = useCallback(async (mediaStream, wsUrl) => {
    if (!wsUrl) throw new Error('useGeminiPCM.start requires wsUrl');
    teardown();
    const attempt = { stream: mediaStream, playbackSources: new Set(), playHead: 0, startedReady: false, ended: false };
    attemptRef.current = attempt;
    const isCurrent = () => attemptRef.current === attempt && !attempt.ended;
    setMuted(false);
    const ready = new Promise((resolve, reject) => {
      attempt.settleStartup = (err) => {
        clearTimeout(attempt.startupTimer);
        attempt.settleStartup = null;
        if (err) reject(err); else resolve();
      };
      attempt.startupTimer = setTimeout(() => attempt.settleStartup?.(new Error('Gemini startup timed out')), 30000);
    });
    // Context construction can fail before the first await attaches a handler.
    ready.catch(() => {});
    try {
      // Capture at 16 kHz, playback at 24 kHz. The worklet downsamples when
      // an older browser falls back to the device's native capture rate.
      const captureCtx = new (window.AudioContext || window.webkitAudioContext)({ sampleRate: INPUT_SAMPLE_RATE });
      attempt.captureCtx = captureCtx;
      const playCtx = new (window.AudioContext || window.webkitAudioContext)({ sampleRate: OUTPUT_SAMPLE_RATE });
      attempt.playCtx = playCtx;
      await Promise.race([captureCtx.audioWorklet.addModule(PCM_WORKLET_URL), ready]);
      if (!isCurrent()) throw new Error('Gemini startup cancelled');

      const src = captureCtx.createMediaStreamSource(mediaStream);
      attempt.captureSrc = src;
      const node = new AudioWorkletNode(captureCtx, 'pcm-encoder', { numberOfInputs: 1, numberOfOutputs: 0 });
      attempt.captureNode = node;
      node.port.onmessage = (ev) => {
        const ws = attempt.ws;
        if (!isCurrent() || !attempt.startedReady || !ev.data || !ws || ws.readyState !== WebSocket.OPEN) return;
        const bytes = new Uint8Array(ev.data);
        try { ws.send(JSON.stringify({ type: 'audio', data: bytesToB64(bytes) })); }
        catch { /* WS closed mid-send; ignore */ }
      };
      src.connect(node);
      // No connection to destination: never play the microphone locally.
      const ws = new WebSocket(wsUrl);
      attempt.ws = ws;
      ws.onopen = () => { if (isCurrent()) callHandler('onStatus', 'connected'); };
      ws.onerror = () => { if (isCurrent()) attempt.settleStartup?.(new Error('WebSocket error')); };
      ws.onclose = (e) => {
        if (!isCurrent()) return;
        if (!attempt.startedReady) attempt.settleStartup?.(new Error('WebSocket closed before ready'));
        else callHandler('onClose', e);
      };
      ws.onmessage = (m) => {
        if (!isCurrent()) return;
        let frame;
        try { frame = JSON.parse(m.data); } catch { return; }
        switch (frame.type) {
          case 'ready':
            attempt.startedReady = true;
            attempt.settleStartup?.();
            break;
          case 'audio':
            schedulePlayback(frame.data, attempt);
            break;
          case 'interrupted':
            for (const source of attempt.playbackSources) { try { source.stop(); } catch {} }
            attempt.playbackSources.clear();
            attempt.playHead = playCtx.currentTime;
            callHandler('onInterrupted');
            break;
          case 'turn_complete':
            break;
          case 'going_away':
            callHandler('onStatus', 'ending');
            break;
          case 'usage':
            callHandler('onUsage', frame.usage);
            break;
          case 'class_change':
            callHandler('onClassChange', frame.change);
            break;
          case 'error':
            if (!attempt.startedReady) attempt.settleStartup?.(new Error(frame.message || 'realtime error'));
            else callHandler('onError', new Error(frame.message || 'realtime error'));
            break;
          default:
            break;
        }
      };
      await ready;
    } catch (err) {
      teardown(attempt);
      throw err;
    }
  }, [teardown]);

  // Each attempt has its own gapless playback queue and active source set.
  const schedulePlayback = (b64, attempt) => {
    const ctx = attempt.playCtx;
    const bytes = b64ToBytes(b64);
    const int16 = new Int16Array(bytes.buffer, bytes.byteOffset, Math.floor(bytes.byteLength / 2));
    const f32 = pcm16ToFloat32(int16);
    if (f32.length === 0) return;
    const buf = ctx.createBuffer(1, f32.length, OUTPUT_SAMPLE_RATE);
    buf.getChannelData(0).set(f32);
    const src = ctx.createBufferSource();
    src.buffer = buf;
    attempt.playbackSources.add(src);
    src.onended = () => {
      attempt.playbackSources.delete(src);
      src.disconnect();
    };
    src.connect(ctx.destination);
    const start = Math.max(ctx.currentTime, attempt.playHead);
    src.start(start);
    attempt.playHead = start + buf.duration;
  };

  // Toggle mute via the same path the manual control uses.
  const toggleMute = useCallback(() => { setMutedBoth(!isMuted); }, [isMuted, setMutedBoth]);

  // sendControlFrame — used by the parent hook to forward non-audio frames
  // (location_update, future control frames) over the same server WS. Drops
  // silently when the WS is closed; callers should already check call state.
  const sendControlFrame = useCallback((frame) => {
    const ws = attemptRef.current?.ws;
    if (!ws || ws.readyState !== WebSocket.OPEN) return false;
    try { ws.send(JSON.stringify(frame)); return true; }
    catch { return false; }
  }, []);

  // Explicit end — send {type:'end'} then teardown; server will close after.
  const end = useCallback(() => {
    try {
      const ws = attemptRef.current?.ws;
      if (ws && ws.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify({ type: 'end' }));
      }
    } catch {}
    teardown();
  }, [teardown]);

  // Component unmount cleanup.
  useEffect(() => () => teardown(), [teardown]);

  return { start, end, toggleMute, sendControlFrame, isMuted };
}
